import type { Pack3DItem } from "./types";

/** A candidate 3D placement: one item, one yaw rotation, at one (x, y, z). */
export interface Placement3D {
  itemIdx: number;
  value: number;
  stackable: boolean;
  x: number;
  y: number;
  z: number;
  /** Footprint after rotation (w along x, d along y), height h. */
  w: number;
  d: number;
  h: number;
}

export interface Enumerated3D {
  placements: Placement3D[];
  /** "x,y,z" -> indices of placements occupying that cell. */
  cellCover: Map<string, number[]>;
  /** itemIdx -> indices of that item's placements. */
  itemPlace: number[][];
}

export const cellKey = (x: number, y: number, z: number): string => x + "," + y + "," + z;

/** The cells a placement occupies, as "x,y,z" keys. */
export function placementCells(p: { x: number; y: number; z: number; w: number; d: number; h: number }): string[] {
  const out: string[] = [];
  for (let dz = 0; dz < p.h; dz++)
    for (let dy = 0; dy < p.d; dy++)
      for (let dx = 0; dx < p.w; dx++) out.push(cellKey(p.x + dx, p.y + dy, p.z + dz));
  return out;
}

/** The footprint of a placement, as [x, y] pairs. */
export function footprint(p: { x: number; y: number; w: number; d: number }): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let dy = 0; dy < p.d; dy++)
    for (let dx = 0; dx < p.w; dx++) out.push([p.x + dx, p.y + dy]);
  return out;
}

/**
 * Enumerate every (yaw rotation × position × layer) placement of every item,
 * then prune placements that can never be supported: a placement with its
 * bottom at layer z > 0 needs, under every footprint tile, at least one
 * surviving *stackable* placement occupying the cell directly below. Pruning
 * runs to a fixpoint (killing a stackable placement can strand placements
 * above it), so the returned set contains exactly the columns the MILP needs.
 */
export function enumeratePlacements3D(
  items: Pack3DItem[],
  cols: number,
  rows: number,
  maxH: number,
): Enumerated3D {
  const placements: Placement3D[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.h > maxH) continue;
    const rots: Array<[number, number]> = it.w === it.d ? [[it.w, it.d]] : [[it.w, it.d], [it.d, it.w]];
    for (const [w, d] of rots) {
      for (let z = 0; z + it.h <= maxH; z++)
        for (let y = 0; y + d <= rows; y++)
          for (let x = 0; x + w <= cols; x++)
            placements.push({ itemIdx: i, value: it.value, stackable: it.stackable, x, y, z, w, d, h: it.h });
    }
  }

  // "x,y,z" -> indices of *stackable* placements occupying that cell.
  const stackCover = new Map<string, number[]>();
  for (let p = 0; p < placements.length; p++) {
    if (!placements[p].stackable) continue;
    for (const k of placementCells(placements[p])) {
      const a = stackCover.get(k);
      if (a) a.push(p); else stackCover.set(k, [p]);
    }
  }

  const alive = new Array<boolean>(placements.length).fill(true);
  let changed = true;
  while (changed) {
    changed = false;
    for (let p = 0; p < placements.length; p++) {
      if (!alive[p] || placements[p].z === 0) continue;
      const pl = placements[p];
      const ok = footprint(pl).every(([x, y]) =>
        (stackCover.get(cellKey(x, y, pl.z - 1)) ?? []).some((q) => alive[q] && q !== p));
      if (!ok) { alive[p] = false; changed = true; }
    }
  }

  const kept = placements.filter((_, p) => alive[p]);
  const cellCover = new Map<string, number[]>();
  for (let p = 0; p < kept.length; p++) {
    for (const k of placementCells(kept[p])) {
      const a = cellCover.get(k);
      if (a) a.push(p); else cellCover.set(k, [p]);
    }
  }
  const itemPlace: number[][] = Array.from({ length: items.length }, () => []);
  for (let p = 0; p < kept.length; p++) itemPlace[kept[p].itemIdx].push(p);
  return { placements: kept, cellCover, itemPlace };
}

/**
 * Independent feasibility referee for a loaded deck. Returns a list of
 * violated rules (empty = feasible). Used by tests to cross-check the MILP
 * and by the game to prove its interactive placement rule agrees with the
 * solver's model — the two must accept exactly the same configurations.
 */
export function packing3DViolations(
  placed: Array<{ x: number; y: number; z: number; w: number; d: number; h: number; stackable: boolean }>,
  cols: number,
  rows: number,
  maxH: number,
): string[] {
  const out: string[] = [];
  const occ = new Map<string, number>(); // cell -> index into `placed`
  placed.forEach((p, i) => {
    if (p.x < 0 || p.y < 0 || p.z < 0 || p.x + p.w > cols || p.y + p.d > rows || p.z + p.h > maxH)
      out.push(`piece ${i} out of bounds`);
    for (const k of placementCells(p)) {
      if (occ.has(k)) out.push(`pieces ${occ.get(k)} and ${i} overlap at ${k}`);
      occ.set(k, i);
    }
  });
  placed.forEach((p, i) => {
    if (p.z === 0) return;
    for (const [x, y] of footprint(p)) {
      const below = occ.get(cellKey(x, y, p.z - 1));
      if (below === undefined) out.push(`piece ${i} has empty space beneath tile (${x},${y})`);
      else if (!placed[below].stackable) out.push(`piece ${i} rests on non-stackable piece ${below} at (${x},${y})`);
    }
  });
  return out;
}
